import { Col } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import { FormattedMessage, FormattedNumber, injectIntl } from 'react-intl';
import { Div, FormItem, FormText, Input, Row, SapId } from 'ui';
// eslint-disable-next-line
import ReferenceDataDropdown from 'common/reference-data/containers/reference-data-dropdown-container';
import UserStatusDropdown from 'common/reference-data/containers/user-status-dropdown-container';
import { referenceTypes } from '../../../utils/app-configs';

const General = ({
  data: {
    number,
    name,
    serialNumber,
    statuses = [],
    bundle,
    isLeader,
    contractType,
    simpleAge,
    estimatedRunningHoursPerYear,
    actualRunningHours,
    actualRunningHoursPerYear,
    materialNumber,
    owner,
    smartlinkIndicator,
    wbsElementDescription,
    wbsElement,
    assetNumber,
    countryOfEquipmentId = null,
    countryOfEquipmentDescription,
  },
  form,
  setFormDirty,
  intl,
  isEditable,
  disabled,
  userStatuses = [],
}) => {
  const onStatusesChanged = (selectedStatuses) => {
    form.setFieldsValue({ statuses: selectedStatuses });
  };

  const onEquimentCountriesChanged = (selectedEquimentCountries) => {
    form.setFieldsValue({ countryOfEquipment: selectedEquimentCountries?.value });
  };

  const nameValidator = form.getFieldDecorator('name', {
    initialValue: name,
  });

  const statusesValidator = form.getFieldDecorator('statuses', {
    initialValue: statuses || [],
  });

  const equipmentCountriesValidator = form.getFieldDecorator('countryOfEquipment', {
    initialValue: countryOfEquipmentId,
  });

  return (
    <Div data-test-id='info-general' marginBottom={20}>
      <Row type='flex' gutter={16} marginRight={0}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.DESCRIPTION' })}>
            {isEditable ?
              nameValidator(<Input disabled={disabled} />)
              :
              <FormText data-test-id='info-name'>{name}</FormText>
            }
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.EQUIPMENT_NR' })}>
            {/* use the ID component to tansform to 'readable' ID (without leading zeroes) */}
            <SapId originalId={number}>{id => <FormText>{id}</FormText>}</SapId>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16} marginRight={0}>
        <Col md={12} sm={24} xs={24} data-test-id='general.serial-nr'>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.SERIAL_NR' })}>
            <FormText>{serialNumber}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.MATERIAL_NR' })}>
            <FormText>{materialNumber}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.STATUS' })}>
            {isEditable ?
              statusesValidator(
                <UserStatusDropdown
                  type={referenceTypes.equipment}
                  onStatusesChanged={onStatusesChanged}
                  originalStatuses={statuses}
                  disabled={disabled}
                  userStatuses={userStatuses}
                  setFormDirty={setFormDirty}
                />,
              )
              :
              <FormText>{statuses && statuses?.map(s => s.id).join(', ')}</FormText>
            }
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.BUNDLE' })}>
            <FormText>{bundle}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.LEADER' })}>
            <FormText>
              {isLeader != null && // use (!=) to catch both null and undefined
                <FormattedMessage id={`equipment.general.isLeader.${isLeader}`} />
              }
            </FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.CONTRACT' })}>
            <FormText>{contractType}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.MANUFACTURER' })}>
            <FormText>{owner}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.SMARTLINK' })}>
            <FormText>{smartlinkIndicator}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.AGE' })}>
            <FormText>
              {`${intl.formatNumber(simpleAge || 0, { maximumFractionDigits: 2 })} year(s)`}
            </FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem
            label={intl.formatMessage({ id: 'equipment.general.TOTAL_ACTUAL_RUNNING_HOURS' })}
          >
            <FormText>
              <FormattedNumber value={actualRunningHours || 0} /> hrs
            </FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.RUNNING_ESTIMATED' })}>
            <FormText>
              <FormattedNumber value={estimatedRunningHoursPerYear || 0} /> hrs
            </FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.RUNNING_ACTUAL' })}>
            <FormText>
              <FormattedNumber value={actualRunningHoursPerYear || 0} /> hrs
            </FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.PROJECT_NUMBER' })}>
            <FormText>{wbsElement}</FormText>
          </FormItem>
        </Col>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.WBS_DESCRIPTION' })}>
            <FormText>{wbsElementDescription}</FormText>
          </FormItem>
        </Col>
      </Row>
      <Row type='flex' gutter={16}>
        <Col md={12} sm={24} xs={24}>
          <FormItem label={intl.formatMessage({ id: 'equipment.general.ASSET_NUMBER' })}>
            <FormText>{assetNumber}</FormText>
          </FormItem>
        </Col>
      </Row>
      {isToggleEnabled('equipmentCountry') ?
        <Row type='flex' gutter={16}>
          <Col md={12} sm={24} xs={24}>
            <FormItem label={intl.formatMessage({ id: 'equipment.general.COUNTRY_EQUIPMENT' })}>
              {isEditable ?
                equipmentCountriesValidator(
                  <ReferenceDataDropdown
                    placeholder={intl.formatMessage({
                      id: 'equipment.general.COUNTRY_EQUIPMENT',
                    })}
                    onObjectChanged={onEquimentCountriesChanged}
                    disabled={disabled}
                    setFormDirty={setFormDirty}
                    type={referenceTypes.equipment}
                    name='equipmentCountries'
                    valueLabel='description'
                    valueKey='value'
                  />,
                )
                : <FormText>{countryOfEquipmentDescription}</FormText>
              }
            </FormItem>
          </Col>
        </Row>
        : null}
    </Div>
  );
};

General.propTypes = {
  data: PropTypes.object,
  isEditable: PropTypes.bool,
  form: PropTypes.object,
  disabled: PropTypes.bool,
  intl: PropTypes.object,
  setFormDirty: PropTypes.func,
  userStatuses: PropTypes.array,
};

export default injectIntl(General);
